import { useAuthStore } from "@kaplan-labs/up-auth-api-client";
import { useSearchParams } from "next/navigation";
import { Suspense, useEffect } from "react";
import { ErrorBoundary } from "react-error-boundary";

import { logError, useOwner, useTheme } from "@up/data";
import { Animation, Auth, BoundaryFallback, BrowserOnly } from "@up/ui";

import { useCart } from "../CartContext";
import CheckoutAuthenticated from "../CheckoutAuthenticated";
import { Layout } from "../Layout";
import Profiles from "../Profiles";

import type { ErrorInfo } from "react";

const logBoundaryError = (error: unknown, info: ErrorInfo) => {
  logError({
    action: "CHECKOUT_BOUNDARY_ERROR",
    error,
    context: { up_stack: info.componentStack },
  });
};

function Go() {
  const auth = useAuthStore();
  const searchParams = useSearchParams();
  const theme = useTheme();
  const owner = useOwner();
  const cart = useCart();

  const code = searchParams.get("code");
  useEffect(() => {
    if (code) {
      cart.setCouponCodeOnLoad(code.toString());
    }
  }, [code, cart.setCouponCodeOnLoad]);

  if (!auth.currentUser) {
    return (
      <div className="mx-auto pt-10 px-4 max-w-[600px]">
        <div className="mb-8">
          <h1 className={theme.heading1Class}>Checkout</h1>
        </div>
        <Animation type="splash-in">
          <Auth />
        </Animation>
      </div>
    );
  }

  if (owner) {
    return (
      <div className="mx-auto pt-10">
        <CheckoutAuthenticated
          partnerSiteBaseURL={
            typeof window !== "undefined" ? window.location.origin : ""
          }
          stripePublicKey={
            process.env["NEXT_PUBLIC_STRIPE_PUBLIC_KEY"] as string
          }
        />
      </div>
    );
  } else {
    return (
      <div className="mx-auto pt-8">
        <Layout headingClass={theme.heading1Class}>
          <Profiles />
        </Layout>
      </div>
    );
  }
}

// Quick fix if we need to take the store down
// export function Checkout() {
//   const theme = useTheme();
//   return (
//     <div className="mx-auto pt-6 max-w-[600px]">
//       <div className="mb-8">
//         <h1 className={theme.heading1Class}>Checkout</h1>
//       </div>
//       <p className={theme.heading2Class}>Pardon our dust</p>
//       We are currently upgrading our checkout experience. Please return in 10 minutes.
//     </div>
//   );
// }

export function Checkout() {
  return (
    <Suspense>
      <BrowserOnly>
        <ErrorBoundary
          FallbackComponent={BoundaryFallback}
          onError={logBoundaryError}
        >
          <Go />
        </ErrorBoundary>
      </BrowserOnly>
    </Suspense>
  );
}
